import { memo, lazy, Suspense } from 'react';
import { Router } from '@reach/router';
import GuardRoute from '@router/GuardRoute';
import { ROUTES } from '@utils/constants';
import { importRetry } from '@router/utils';

// lazy imports for code splitting the entire app at the route level
const Agenda = lazy(() =>
    importRetry(() => import('@pages/mobile/agenda/Agenda')),
);
const ActivityEdit = lazy(() =>
    importRetry(() => import('@pages/mobile/activityEdit/ActivityEdit')),
);
const BookingEdit = lazy(() =>
    importRetry(() => import('@pages/mobile/bookingEdit/BookingEdit')),
);
const BookingCreation = lazy(() =>
    importRetry(() => import('@pages/mobile/bookingCreation/BookingCreation')),
);
const Settings = lazy(() =>
    importRetry(() => import('@pages/mobile/settings/Settings')),
);
const Customers = lazy(() =>
    importRetry(() => import('@pages/mobile/customers/Customers')),
);
const Customer = lazy(() =>
    importRetry(() => import('@pages/mobile/customer/Customer')),
);
const CustomerSelect = lazy(() =>
    importRetry(() => import('@pages/mobile/customerSelect/CustomerSelect')),
);
const Login = lazy(() =>
    importRetry(() => import('@pages/mobile/login/Login')),
);
const CustomerCreation = lazy(() =>
    importRetry(
        () => import('@pages/mobile/customerCreation/CustomerCreation'),
    ),
);
const CustomerEdit = lazy(() =>
    importRetry(() => import('@pages/mobile/customerEdit/CustomerEdit')),
);
const DiscountEdit = lazy(() =>
    importRetry(() => import('@pages/mobile/discountEdit/DiscountEdit')),
);
const DiscountCreation = lazy(() =>
    importRetry(
        () => import('@pages/mobile/discountCreation/DiscountCreation'),
    ),
);
const ForgetPassword = lazy(() =>
    importRetry(() => import('@pages/mobile/forgetPassword/ForgetPassword')),
);
const ActivityCreation = lazy(() =>
    importRetry(
        () => import('@pages/mobile/activityCreation/ActivityCreation'),
    ),
);
const TreatmentsEdit = lazy(
    () => import('@pages/mobile/treatmentsEdit/TreatmentsEdit'),
);
const Notifications = lazy(() =>
    importRetry(
        () => import('@pages/mobile/notifications/MobileNotifications'),
    ),
);
const Catalog = lazy(() => import('@pages/mobile/catalog/Catalog'));

const { PUBLIC_URL } = process.env;
const redirectIfLogged = (isAuthed: boolean) => !isAuthed;

const MobileRoutes = (): JSX.Element => (
    <Suspense fallback={<div />}>
        <Router basepath={PUBLIC_URL || '/'}>
            {/* LOGIN */}
            <GuardRoute
                condition={redirectIfLogged}
                component={Login}
                redirect={ROUTES.AGENDA}
                path={ROUTES.LOGIN}
            />
            <GuardRoute
                condition={redirectIfLogged}
                component={ForgetPassword}
                redirect={ROUTES.AGENDA}
                path={ROUTES.LOGIN_FORGET_PASSWORD}
            />

            {/* AGENDA */}
            <GuardRoute default component={Agenda} path={ROUTES.AGENDA} />
            <GuardRoute component={Agenda} path={ROUTES.AGENDA} />
            <GuardRoute
                component={BookingEdit}
                path={`${ROUTES.AGENDA_EDIT_BOOKING}/:id`}
            />
            <GuardRoute
                component={ActivityEdit}
                path={`${ROUTES.AGENDA_EDIT_ACTIVITY}/:activityId`}
            />
            <GuardRoute
                component={DiscountEdit}
                path={`${ROUTES.AGENDA_EDIT_DISCOUNT}/:discountId`}
            />
            <GuardRoute
                component={DiscountCreation}
                path={ROUTES.AGENDA_CREATE_DISCOUNT}
            />
            <GuardRoute
                component={ActivityCreation}
                path={ROUTES.AGENDA_CREATE_ACTIVITY}
            />
            <GuardRoute
                component={BookingCreation}
                path={ROUTES.AGENDA_CREATE_BOOKING}
            />
            <GuardRoute
                component={CustomerSelect}
                path={ROUTES.AGENDA_CREATE_BOOKING_CUSTOMER_SELECT}
            />
            <GuardRoute
                component={Catalog}
                path={ROUTES.AGENDA_CREATE_BOOKING_TREATMENTS}
            />
            <GuardRoute
                component={TreatmentsEdit}
                path={`${ROUTES.AGENDA_CREATE_BOOKING_TREATMENTS_EDIT}/:treatmentId`}
            />
            <GuardRoute
                component={Notifications}
                path={ROUTES.AGENDA_NOTIFICATIONS}
            />

            {/* SETTINGS */}
            <GuardRoute component={Settings} path={ROUTES.SETTINGS} />

            {/* CUSTOMERS */}
            <GuardRoute component={Customers} path={ROUTES.CUSTOMERS_LIST} />
            <GuardRoute
                component={Customer}
                path={`${ROUTES.CUSTOMERS_DETAILS}/:customerId`}
            />
            <GuardRoute
                component={CustomerCreation}
                path={ROUTES.CUSTOMERS_CREATE}
            />
            <GuardRoute
                component={CustomerEdit}
                path={`${ROUTES.CUSTOMERS_EDIT}/:customerId`}
            />

            <GuardRoute component={Catalog} path={ROUTES.CATALOG} />
        </Router>
    </Suspense>
);

export default memo(MobileRoutes);
